import React from 'react';
import { format } from 'date-fns';
import { formatTime } from '../../../utils/job-utils';
import { 
  EDIT_FIELDS, 
  CHECKIN_HOUR_OPTIONS, 
  CHECKOUT_HOUR_OPTIONS 
} from '../constants/editJobConstants';
import { parseOverrideDateTime } from '../utils/editJobUtils';

const ScheduleBox = ({ 
  type,
  reservation,
  editingField,
  reservationEditData,
  setReservationEditData,
  onEdit,
  onSave,
  onCancel 
}) => {
  const isCheckIn = type === EDIT_FIELDS.CHECKIN;
  const isEditing = editingField === type;
  
  // Check for override times first, then fall back to regular times
  const getDisplayDateTime = () => {
    const overrideDate = isCheckIn ? reservation?.override_check_in : reservation?.override_check_out;
    
    if (overrideDate) {
      // Parse override date to get separate date and time
      const parsed = parseOverrideDateTime(overrideDate);
      return {
        date: parsed.date ? new Date(parsed.date + 'T00:00:00') : null,
        time: parsed.time || (isCheckIn ? '15:00' : '11:00')
      };
    } else {
      // Use regular date and time
      const regularDate = isCheckIn ? reservation?.check_in : reservation?.check_out;
      const regularTime = isCheckIn ? reservation?.check_in_time : reservation?.check_out_time;
      return {
        date: regularDate ? new Date(regularDate) : null,
        time: regularTime
      };
    }
  };
  
  const { date, time } = getDisplayDateTime();
  
  const handleDateChange = (e) => {
    const field = isCheckIn ? 'checkInDate' : 'checkOutDate';
    setReservationEditData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const snapToHour = (time) => {
    if (!time) return time;
    const hour = time.split(':')[0];
    return `${hour}:00`;
  };

  const handleTimeChange = (e) => {
    const field = isCheckIn ? 'checkInTime' : 'checkOutTime';
    setReservationEditData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const hourOptions = isCheckIn ? CHECKIN_HOUR_OPTIONS : CHECKOUT_HOUR_OPTIONS;

  const getCurrentDate = () => {
    const field = isCheckIn ? 'checkInDate' : 'checkOutDate';
    return reservationEditData[field];
  };

  const getCurrentTime = () => {
    const field = isCheckIn ? 'checkInTime' : 'checkOutTime';
    return reservationEditData[field];
  };

  return (
    <div className="bg-white p-2 rounded-md border border-gray-200">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">
          {isCheckIn ? 'Check-in' : 'Check-out'}
        </p>
        <button 
          type="button"
          onClick={() => onEdit(type)}
          disabled={editingField && editingField !== type}
          className="text-xs bg-blue-600 text-white hover:bg-blue-700 font-medium px-3 py-1.5 rounded shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400"
        >
          Edit
        </button>
      </div>
      
      <div className="space-y-2 mb-2">
        <div className="flex items-center space-x-2">
          <svg 
            className={`h-4 w-4 ${isCheckIn ? 'text-blue-600' : 'text-red-500'}`} 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" 
            />
          </svg>
          <p className="text-gray-800 font-medium text-sm">
            {date ? format(new Date(date), "MMM d, yyyy") : 'N/A'}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <svg 
            className={`h-4 w-4 ${isCheckIn ? 'text-blue-600' : 'text-red-500'}`} 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" 
            />
          </svg>
          <p className="text-gray-800 font-medium text-sm">
            {time ? formatTime(time) : (isCheckIn ? '3:00 PM' : '11:00 AM')}
          </p>
        </div>
      </div>
      
      {/* Inline edit fields */}
      {isEditing && (
        <div className="pt-2 border-t border-gray-200 space-y-2">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {isCheckIn ? 'Check-in' : 'Check-out'} Date
            </label>
            <input 
              type="date"
              value={getCurrentDate()}
              onChange={handleDateChange}
              className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {isCheckIn ? 'Check-in' : 'Check-out'} Time
            </label>
            <select
              value={snapToHour(getCurrentTime())}
              onChange={handleTimeChange}
              className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              {hourOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {!isCheckIn && (
              <p className="text-xs text-gray-500 mt-1">Allowed: 10:00 AM – 4:00 PM</p>
            )}
          </div>
          <div className="flex space-x-2">
            <button 
              type="button"
              onClick={() => onSave(type)}
              className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 transition-colors"
            >
              Save
            </button>
            <button 
              type="button"
              onClick={onCancel}
              className="text-xs bg-gray-300 text-gray-700 px-3 py-1 rounded hover:bg-gray-400 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleBox;